import React, {
  ReactElement,
  useState,
  useEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import {
  BrowserRouter,
  Route,
  RouteComponentProps,
} from 'react-router-dom';
import {
  connectHits,
} from 'react-instantsearch-dom';
import axios from 'axios';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import {
  MESSAGES_ADMIN_PREFIX,
  ALGOLIA_MESSAGES_INDEX,
  MESSAGES_BROWSER_API_PREFIX,
} from '../../../utils/constants';
import DateHelper from '../../../utils/DateHelper';
import AdminURLSync from '../AdminURLSync';

import useBulkSelected from '../../../utils/hooks/useBulkSelected';
import useModal from '../../../utils/hooks/useModal';
import ConfirmationModal from '../widgets/ConfirmationModal';
import DateFilterDisplay from '../widgets/filters/display-components/DateFilterDisplay';
import { MessageInterface, SearchState } from './interfaces';
import AdminDashboardTableHits from './AdminDashboardTableHits';
import AdminDashboardTableHit from './AdminDashboardTableHit';

const Hit: React.FC<MessageInterface> = ({
  id,
  updated_at,
  from_user_name,
  from_user_email,
  from_seller_name,
  to_user_name,
  to_seller_name,
  is_spam_for_recipient,
  archived_for_recipient,
  ip,
  selected,
  toggleSelected,
}) => {
  const [isHidden, setIsHidden] = useState(false);
  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
  } = useModal();

  const fetchBGColor = () => {
    if (archived_for_recipient) {
      return 'bg-light-red';
    }
    if (is_spam_for_recipient) {
      return 'bg-spam';
    }
    return '';
  };

  const updateModal = (type: string) => {
    switch (type) {
      case 'delete':
        setModalData({
          message: 'Would you like to delete this message?',
          onConfirmAsync: () => {
            return new Promise((res, rej) => {
              axios.delete(`${MESSAGES_ADMIN_PREFIX}/${id}`)
                .then(({ data }) => {
                  res(data.message);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => {
            setIsHidden(true);
          },
        });
        break;
      default:
        setModalData({
          message: '',
          onClose: () => {},
        });
    }
  };

  // TODO: update `any`
  const values: any = [
    {
      key: 'delete-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          href="#"
          className="mx-2"
          aria-label={`Delete order number ${id}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
    {
      key: 'to',
      value: (
        <p className="m-0">
          <a
            className="text-black m-0 font-weight-bold"
            href={`/admin/messages/${id}`}
          >
            <span>
              {to_user_name || 'Anonymous'}
            </span>
            <>
              {to_seller_name && (
                <span>{` (${to_seller_name})`}</span>
              )}
            </>
          </a>
        </p>
      ),
    },
    {
      key: 'from',
      value: (
        <p className="text-table-light-gray m-0">
          <span>
            {from_user_email || from_seller_name}
          </span>
        </p>
      ),
    },
    {
      key: 'from_name',
      value: (
        <p className="text-table-light-gray m-0">
          {from_user_name || 'Anonymous'}
        </p>
      ),
    },
    {
      key: 'ip',
      value: (
        <p className="text-table-light-gray m-0">
          {ip || ''}
        </p>
      ),
    },
    {
      key: 'date',
      value: (
        <p className="text-table-light-gray m-0">
          {DateHelper.getLocalizedDateTimeFromTimestamp(updated_at).format('YYYY/MM/DD [at] h:mm a')}
        </p>
      ),
    },
    {
      key: 'delete',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          href="#"
          className="widescreen mx-2"
          aria-label={`Delete order number ${id}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
  ];

  return (
    <>
      {!isHidden && (
        <AdminDashboardTableHit
          rowWrapperClass={fetchBGColor()}
          values={values}
          id={id}
          selected={selected}
          toggleSelected={toggleSelected}
        />
      )}
      {!isHidden && (
        <ConfirmationModal
          show={showModal}
          setShow={setShowModal}
          {...modalData}
        />
      )}
    </>
  );
};

const Hits: React.FC<{
  hits: any,
  selected: Array<number>,
  toggleSelected: (id: number) => void,
}> = ({ hits, selected, toggleSelected }) => {
  return (
    <AdminDashboardTableHits
      hits={hits}
      Hit={Hit}
      selected={selected}
      toggleSelected={toggleSelected}
    />
  );
};

const CustomHits = connectHits(Hits);

const AdminMessages: React.FC<RouteComponentProps> = ({
  history,
  location,
  match,
}): ReactElement => {
  const getAdType = (browserLocation) => {
    const { search } = browserLocation;
    const query = new URLSearchParams(search);
    return query.get('type') || 'all'; // Default to 'all'
  };

  const [externalSearchState, setExternalSearchState] = useState<SearchState>({
    configure: {
      numericFilters: [],
      facetFilters: [],
    },
    query: '',
  });

  const {
    bulkAction,
    setBulkAction,
    updateBulkSelected,
    bulkSelected,
    toggleAllSelected,
    allSelected,
    setHits,
    clearBulkSelected,
  } = useBulkSelected();

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    modalFormState,
    setModalFormState,
    reloadPage,
  } = useModal();

  const performBulkAction = useCallback((action: string) => {
    if (bulkSelected.length < 1) {
      setModalData({
        message: 'No messages have been selected. Please, select one or more messages.',
        isSimple: true,
        onConfirm: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
        onClose: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
      });
    } else {
      switch (action) {
        case 'delete':
          setModalData({
            message: `Would you like to delete ${bulkSelected.length} messages?`,
            onConfirmAsync: () => {
              return new Promise((res, rej) => {
                axios.delete(`${MESSAGES_ADMIN_PREFIX}/${bulkSelected.join(',')}`)
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'mark_spam':
          setModalData({
            message: `Would you like to mark ${bulkSelected.length} messages and their threads as spam for both sender and recipient?`,
            onConfirmAsync: () => {
              return new Promise((res, rej) => {
                axios.post(`${MESSAGES_BROWSER_API_PREFIX}/${bulkSelected.join(',')}/bulk-mark-spam-all`)
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'mark_not_spam':
          setModalData({
            message: `Would you like to remove ${bulkSelected.length} messages and their threads from the spam boxes of both the sender and the recipient?`,
            onConfirmAsync: () => {
              return new Promise((res, rej) => {
                axios.delete(`${MESSAGES_BROWSER_API_PREFIX}/${bulkSelected.join(',')}/bulk-unmark-spam-all`)
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        default:
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
      }
    }
  }, [bulkSelected, modalFormState, reloadPage]);

  useEffect(() => {
    const { value } = bulkAction;
    if (value) {
      performBulkAction(value);
    }
  }, [bulkAction]);

  const tableHeaderRowValues = [
    {
      key: 'to',
      value: 'To',
    },
    {
      key: 'from',
      value: 'From',
    },
    {
      key: 'from_name',
      value: 'From Name',
    },
    {
      key: 'ip',
      value: 'IP Address',
    },
    {
      key: 'date',
      value: 'Sent',
    },
  ];

  return (
    <>
      <AdminURLSync
        title="Messages"
        numEmptyStartHeaders={1}
        numEmptyEndHeaders={1}
        setBulkAction={setBulkAction}
        bulkAction={bulkAction}
        setHits={setHits}
        allSelected={allSelected}
        toggleAllSelected={toggleAllSelected}
        clearBulkSelected={clearBulkSelected}
        bulkActions={[
          {
            label: 'Delete',
            value: 'delete',
          },
          {
            label: 'Mark As Spam',
            value: 'mark_spam',
          },
          {
            label: 'Mark As Not Spam',
            value: 'mark_not_spam',
          },
        ]}
        externalQueryParams={[]}
        history={history}
        location={location}
        match={match}
        externalSearchState={externalSearchState}
        tableHeaderRowValues={tableHeaderRowValues}
        indexName={ALGOLIA_MESSAGES_INDEX!}
        customHits={(
          <CustomHits
            selected={bulkSelected}
            toggleSelected={updateBulkSelected}
          />
        )}
        attributes={[
          {
            type: 'dateRange',
            attribute: 'updated_at',
            startLabel: '',
            endLabel: '',
            DisplayComponent: <DateFilterDisplay />,
          },
        ]}
        dateAttributes={[]}
        tabs={[]}
      />
      <ConfirmationModal
        show={showModal}
        setShow={setShowModal}
        modalFormState={modalFormState}
        clearBulkSelected={clearBulkSelected}
        {...modalData}
      />
    </>
  );
};

export default AdminMessages;

if (document.getElementById('react-admin-dashboard-table-messages')) {
  const element = document.getElementById('react-admin-dashboard-table-messages');

  ReactDOM.render(
    <BrowserRouter>
      <Route
        path={MESSAGES_ADMIN_PREFIX}
        component={AdminMessages}
      />
    </BrowserRouter>,
    element,
  );
}
